/**
 * @architecture-header 1
 * @layer module-infrastructure
 * @domain settings-general
 * @responsibility Own the backend-authoritative SET-F04 organization-general query and its update
 *   command, and notify the rail switcher's identity display of a successful rename.
 * @state-owner TanStack Query
 * @allowed-imports app environment/session; active-role provider; same-module API and contracts;
 *   shared workspace-identity refresh registry; TanStack Query
 * @forbidden component UI; fabricated organization data; direct Supabase calls; member fields
 * @public-api settings-general hooks
 * @side-effects authenticated organization read/write, cache update, and rail-switcher refresh notification
 * @tests frontend/packages/workspace-web/src/modules/settings-general/components/GeneralSettingsSurface.test.tsx
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VispoApiError } from '@vispo/api-client';
import { useOptionalWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveOrganizationId } from '../../../app/providers/ActiveRoleProvider';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { notifyWorkspaceIdentityRenamed } from '../../../shared/lib/workspace-identity-refresh';
import { loadMeContext } from '../../auth';
import { updateOrganization, uploadOrganizationLogo } from './settings-general';

const DEMO_ORGANIZATION_ID = 'demo-organization';

export interface OrganizationGeneralView {
  readonly organizationId: string | null;
  readonly name: string | null;
  readonly logoUrl: string | null;
  readonly revision: number | null;
  readonly canManage: boolean;
}

export const settingsGeneralKeys = {
  general: (userId: string, organizationId: string) =>
    ['set-f04', 'general', userId, organizationId] as const,
};

function effectiveOrganizationId(
  activeOrganizationId: string | null,
  demoMode: boolean | undefined,
): string | null {
  return activeOrganizationId ?? (demoMode ? DEMO_ORGANIZATION_ID : null);
}

export function useOrganizationGeneral() {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const activeOrganizationId = useActiveOrganizationId();
  const userId = session?.userId ?? 'anonymous';
  const organizationId = effectiveOrganizationId(activeOrganizationId, environment?.demoMode);

  return useQuery({
    queryKey: settingsGeneralKeys.general(userId, organizationId ?? 'none'),
    enabled: Boolean(environment && (session || environment.demoMode)),
    queryFn: async (): Promise<OrganizationGeneralView> => {
      if (environment?.demoMode) {
        return {
          organizationId: DEMO_ORGANIZATION_ID,
          name: 'Demo Organization',
          logoUrl: null,
          revision: 1,
          canManage: true,
        };
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const context = await loadMeContext(environment.apiClient);
      const organization = context.organizations.find(
        (candidate) => candidate.id === organizationId,
      );
      if (!organization) {
        return {
          organizationId: null,
          name: null,
          logoUrl: null,
          revision: null,
          canManage: false,
        };
      }
      return {
        organizationId: organization.id,
        name: organization.name,
        logoUrl: organization.logo_url,
        revision: organization.revision,
        canManage: ['owner', 'admin'].includes(organization.membership_role),
      };
    },
  });
}

export function useUpdateOrganization() {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const activeOrganizationId = useActiveOrganizationId();
  const userId = session?.userId ?? 'anonymous';
  const organizationId = effectiveOrganizationId(activeOrganizationId, environment?.demoMode);
  const queryClient = useQueryClient();
  const queryKey = settingsGeneralKeys.general(userId, organizationId ?? 'none');

  return useMutation({
    mutationFn: async (input: { name: string; clearLogo?: boolean; expectedRevision: number }) => {
      if (!organizationId) throw new Error('No active organization');
      const command = { ...input, idempotencyKey: `agent-a-set-f04-${crypto.randomUUID()}` };
      if (environment?.demoMode) {
        const current = queryClient.getQueryData<OrganizationGeneralView>(queryKey);
        if (!current) throw new Error('組織資料尚未準備完成');
        const forcedConflict = (
          globalThis as typeof globalThis & {
            __VISPO_SET_F04_FORCE_REVISION_CONFLICT__?: boolean;
          }
        ).__VISPO_SET_F04_FORCE_REVISION_CONFLICT__;
        if (forcedConflict || current.revision !== command.expectedRevision) {
          throw new VispoApiError(
            {
              code: 'ORGANIZATION_REVISION_CONFLICT',
              message: 'The organization changed; refresh and retry.',
              details: undefined,
              requestId: 'demo',
            },
            409,
          );
        }
        const updated: OrganizationGeneralView = {
          ...current,
          name: command.name,
          logoUrl: command.clearLogo ? null : current.logoUrl,
          revision: (current.revision ?? 0) + 1,
        };
        return updated;
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const response = await updateOrganization(environment.apiClient, organizationId, command);
      const updated: OrganizationGeneralView = {
        organizationId: response.organization.id,
        name: response.organization.name,
        logoUrl: response.organization.logo_url,
        revision: response.organization.revision,
        canManage: ['owner', 'admin'].includes(response.organization.membership_role),
      };
      return updated;
    },
    onSuccess: (view) => {
      queryClient.setQueryData(queryKey, view);
      notifyWorkspaceIdentityRenamed();
    },
  });
}

export function useUploadOrganizationLogo() {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const activeOrganizationId = useActiveOrganizationId();
  const userId = session?.userId ?? 'anonymous';
  const organizationId = effectiveOrganizationId(activeOrganizationId, environment?.demoMode);
  const queryClient = useQueryClient();
  const queryKey = settingsGeneralKeys.general(userId, organizationId ?? 'none');

  return useMutation({
    mutationFn: async (input: { file: File; expectedRevision: number }) => {
      if (!organizationId) throw new Error('No active organization');
      if (environment?.demoMode) {
        const current = queryClient.getQueryData<OrganizationGeneralView>(queryKey);
        if (!current) throw new Error('組織資料尚未準備完成');
        const updated: OrganizationGeneralView = {
          ...current,
          logoUrl: URL.createObjectURL(input.file),
          revision: (current.revision ?? 0) + 1,
        };
        return updated;
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const response = await uploadOrganizationLogo(
        environment.apiClient,
        organizationId,
        input.file,
        {
          expectedRevision: input.expectedRevision,
          idempotencyKey: `agent-a-set-f11-${crypto.randomUUID()}`,
        },
      );
      const updated: OrganizationGeneralView = {
        organizationId: response.organization.id,
        name: response.organization.name,
        logoUrl: response.organization.logo_url,
        revision: response.organization.revision,
        canManage: ['owner', 'admin'].includes(response.organization.membership_role),
      };
      return updated;
    },
    onSuccess: (view) => {
      queryClient.setQueryData(queryKey, view);
      // The rail switcher (WorkspaceSwitcher.tsx) has no shared TanStack Query cache with this
      // hook's own query key -- same registry SET-F04's own rename already established, reused
      // verbatim rather than inventing a second refresh path.
      notifyWorkspaceIdentityRenamed();
    },
  });
}
